<template>
  <div class="footprint-home-page">
    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">核算标准</span>
        <el-select v-model="standard" size="small" style="width: 220px">
          <el-option
            v-for="item in standards"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">年份</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="选择年份"
          value-format="yyyy"
        />
      </div>
    </filter-bar>

    <div class="annual-section">
      <div class="annual-cards">
        <div class="annual-card count">
          <div class="card-label">年度核算产品数</div>
          <div class="card-value">
            {{ annualStats.productCount.value }}
            <span class="unit">个</span>
          </div>
          <div class="card-yoy">
            <i :class="annualStats.productCount.yoy >= 0 ? 'el-icon-top text-danger' : 'el-icon-bottom text-success'"></i>
            较上年 {{ Math.abs(annualStats.productCount.yoy) }}%
          </div>
        </div>
        <div class="annual-card footprint">
          <div class="card-label">平均碳足迹强度</div>
          <div class="card-value">
            {{ formatNumber(annualStats.avgFootprint.value, 2) }}
            <span class="unit">{{ annualStats.avgFootprint.unit }}</span>
          </div>
          <div class="card-yoy">
            <i :class="annualStats.avgFootprint.yoy >= 0 ? 'el-icon-top text-danger' : 'el-icon-bottom text-success'"></i>
            较上年 {{ Math.abs(annualStats.avgFootprint.yoy) }}%
          </div>
        </div>
      </div>
      <chart-card title="月度核算完成趋势" class="monthly-chart">
        <base-chart :option="monthlyChartOption" />
      </chart-card>
    </div>

    <div class="bottom-section">
      <chart-card title="产品碳足迹排名" class="ranking-chart">
        <base-chart :option="rankingChartOption" />
      </chart-card>
      <chart-card title="生命周期阶段贡献" class="stage-chart">
        <base-chart :option="stageChartOption" />
      </chart-card>
    </div>

    <div class="table-section">
      <div class="table-header">
        <h3 class="table-title">最近核算记录</h3>
        <div class="quick-actions">
          <el-button size="small" @click="goTo('/carbon-footprint/model')">新建模型</el-button>
          <el-button size="small" @click="goTo('/carbon-footprint/accounting')">发起核算</el-button>
          <el-button size="small" type="primary" @click="goTo('/carbon-footprint/evaluation')">生成报告</el-button>
        </div>
      </div>
      <el-table :data="recentRecords" border stripe size="small">
        <el-table-column prop="productName" label="产品名称" min-width="160" />
        <el-table-column prop="functionalUnit" label="功能单位" width="100" />
        <el-table-column prop="footprint" label="碳足迹 (kgCO2e)" width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.footprint, 2) }}</template>
        </el-table-column>
        <el-table-column prop="standard" label="核算标准" min-width="200" />
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)" size="small">{{ row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="completedAt" label="完成时间" width="120" />
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope>
            <el-button type="text" size="small" @click="goTo('/carbon-footprint/model')">查看模型</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getHomeData } from '@/api/modules/carbonFootprint'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonFootprintHome',
  components: { FilterBar, ChartCard, BaseChart },
  data() {
    return {
      standards: [],
      standard: 'ISO 14067',
      year: '2025',
      annualStats: {
        productCount: { value: 0, yoy: 0 },
        avgFootprint: { value: 0, unit: 'kgCO2e/件', yoy: 0 }
      },
      recentRecords: [],
      monthlyChartOption: {},
      rankingChartOption: {},
      stageChartOption: {}
    }
  },
  watch: {
    standard() {
      this.loadData()
    },
    year() {
      this.loadData()
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getHomeData({ year: this.year, standard: this.standard })
      const data = res.data
      this.standards = data.standards
      if (!this.standards.includes(this.standard)) {
        this.standard = data.standards[0]
      }
      if (data.year) {
        this.year = String(data.year)
      }
      this.annualStats = data.annualStats
      this.recentRecords = data.recentRecords
      this.buildMonthlyChart(data.monthlyTrend)
      this.buildRankingChart(data.productRanking)
      this.buildStageChart(data.stageContribution)
    },
    statusTagType(status) {
      const map = { '已完成': 'success', '进行中': 'warning', '待审核': 'info' }
      return map[status] || ''
    },
    goTo(path) {
      this.$router.push(path)
    },
    buildMonthlyChart(trend) {
      this.monthlyChartOption = {
        tooltip: { trigger: 'axis' },
        grid: { left: 50, right: 30, top: 30, bottom: 30 },
        xAxis: { type: 'category', data: trend.months },
        yAxis: { type: 'value', name: '产品数', minInterval: 1 },
        series: [{
          name: '完成核算产品数',
          type: 'line',
          data: trend.completedCount,
          smooth: true,
          symbol: 'circle',
          lineStyle: { color: '#409EFF', width: 2 },
          itemStyle: { color: '#409EFF' },
          areaStyle: { color: 'rgba(64, 158, 255, 0.12)' }
        }]
      }
    },
    buildRankingChart(ranking) {
      const names = ranking.map(item => item.name).reverse()
      const values = ranking.map(item => item.footprint).reverse()
      this.rankingChartOption = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 140, right: 40, top: 10, bottom: 30 },
        xAxis: { type: 'value', name: 'kgCO2e' },
        yAxis: { type: 'category', data: names, axisLabel: { width: 120, overflow: 'truncate' } },
        series: [{
          type: 'bar',
          data: values,
          itemStyle: { color: '#1a3a5c' },
          barMaxWidth: 20
        }]
      }
    },
    buildStageChart(stages) {
      this.stageChartOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {c}% ({d}%)' },
        legend: { orient: 'vertical', right: 10, top: 'center' },
        color: ['#1a3a5c', '#409EFF', '#E6A23C', '#67C23A', '#909399'],
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['40%', '50%'],
          data: stages,
          label: { formatter: '{b}\n{d}%' }
        }]
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.annual-section {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  height: 320px;
}

.annual-cards {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
}

.annual-card {
  flex: 1;
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.count {
    background: linear-gradient(135deg, #fff 60%, rgba(64, 158, 255, 0.08));
  }

  &.footprint {
    background: linear-gradient(135deg, #fff 60%, rgba(103, 194, 58, 0.08));
  }

  .card-label {
    font-size: 14px;
    color: @text-secondary;
    margin-bottom: 12px;
  }

  .card-value {
    font-size: 28px;
    font-weight: 700;
    color: @text-primary;

    .unit {
      font-size: 14px;
      font-weight: 400;
      color: @text-secondary;
      margin-left: 4px;
    }
  }

  .card-yoy {
    margin-top: 12px;
    font-size: 13px;
    color: @text-secondary;
  }
}

.monthly-chart {
  flex: 1;
}

.bottom-section {
  display: flex;
  gap: 16px;
  height: 360px;
  margin-bottom: 16px;

  .ranking-chart,
  .stage-chart {
    flex: 1;
  }
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  overflow-x: auto;

  .table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .table-title {
    font-size: 15px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }

  .quick-actions {
    display: flex;
    gap: 8px;
  }
}
</style>
